import { Component, h, Prop, State, Element, Build } from '@stencil/core';

const COMPONENT_NAME = 'long-content-type-5';

/**
 * Feature carousel component with navigation controls (arrows and dots).
 * Displays feature descriptions in a slider format with media on the left and content on the right.
 *
 * Supports two modes:
 * 1. How-To mode: Use `items`, `title`, and `subtitle` props (or `values` object)
 * 2. Standard mode: Use `slides` prop with heading and description slots
 *
 * @product signnow
 * @category Long Content
 * @source howto
 * @slot heading - {required}, <me-heading variant="2">
 * @slot description - {required}, <me-text variant="medium">
 * @example How-To mode with separate props:
 *   <long-content-type-5
 *     title="How to Sign a PDF Online"
 *     subtitle="Follow these simple steps"
 *     items='[{"question": "Upload", "answer": "Upload your document...", "mediaId": "01"}]'
 *   ></long-content-type-5>
 * @example How-To mode with values object:
 *   <long-content-type-5
 *     values='{"title": "How to Sign", "subTitle": "Simple steps", "items": [{"question": "Upload", "answer": "Upload...", "mediaId": "01"}]}'
 *   ></long-content-type-5>
 * @example Standard mode:
 *   <long-content-type-5
 *     slides='[{"heading": "Long heading", "text": "Lorem ipsum...", "media": "https://example.com/image.jpg"}]'
 *   >
 *     <me-heading slot="heading" variant="2">Offer letter signature</me-heading>
 *     <me-text slot="description" variant="medium">Your entire business wins.</me-text>
 *   </long-content-type-5>
 */
@Component({
  tag: 'long-content-type-5',
  styleUrl: 'index.scss',
  shadow: false,
})
export class LongContentType5 {
  @Element() el: HTMLElement;

  /**
   * Array of feature slides to be displayed in the carousel.
   * @type {array}
   * @slides.type {object}
   * @slides.property {string} heading - Feature heading text
   * @slides.property {string} text - Feature description text
   * @slides.property {string} media - Feature image URL
   * @slides.required heading
   * @slides.required text
   * @slides.required media
   * @minItems 1
   */
  @Prop() slides?: any[];

  /**
   * Enable/disable carousel autoplay
   * @default false
   */
  @Prop() autoplay: boolean = false;

  /**
   * Autoplay interval in milliseconds
   * @default 5000
   */
  @Prop() autoplayInterval: number = 5000;

  /**
   * Data payload for How-To decoration mode
   * @dynamic
   */
  @Prop({ mutable: true }) values?: {
    title?: string;
    subTitle?: string;
    items?: Array<{
      question?: string;
      answer?: string;
      raw_html?: string; // Optimized for SSR: bypasses innerHTML parsing
      mediaId?: string;
    }>;
  };

  /**
   * Title for the section (alternative to values.title)
   * @dynamic
   */
  @Prop({ attribute: 'title' }) heading?: string;

  /**
   * Subtitle for the section (alternative to values.subTitle)
   * @dynamic
   */
  @Prop() subtitle?: string;

  /**
   * Array of items (alternative to values.items)
   * @dynamic
   */
  @Prop({ mutable: true }) items?: Array<{
    question?: string;
    answer?: string;
    raw_html?: string; // Optimized for SSR: bypasses innerHTML parsing
    mediaId?: string;
  }>;

  /**
   * HTML tag for the main heading element (h1, h2, h3, h4, h5, h6)
   * @default "h2"
   */
  @Prop() headingTag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h2';

  /**
   * HTML tag for the item heading element (h1, h2, h3, h4, h5, h6)
   * @default "h3"
   */
  @Prop() itemHeadingTag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h3';

  @State() currentSlide: number = 0;
  @State() isTransitioning: boolean = false;

  private autoplayTimer?: number;
  private touchStartX: number = 0;
  private touchEndX: number = 0;

  private decodeHtmlEntities(value: string): string {
    return value
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  }

  private parseJsonString<T>(value: string): T | null {
    try {
      return JSON.parse(this.decodeHtmlEntities(value)) as T;
    } catch {
      return null;
    }
  }

  parseValues() {
    // Parse items array if it comes as string from prop or attribute.
    // Backend often sends HTML-encoded JSON in attributes.
    if (!Array.isArray(this.items)) {
      const itemsRaw = typeof this.items === 'string' ? this.items : this.el.getAttribute('items');
      if (itemsRaw) {
        const parsedItems = this.parseJsonString<
          Array<{
            question?: string;
            answer?: string;
            raw_html?: string;
            mediaId?: string;
          }>
        >(itemsRaw);
        if (parsedItems && Array.isArray(parsedItems)) {
          this.items = parsedItems;
        } else {
          console.error('Failed to parse items:', itemsRaw);
        }
      }
    }

    // Parse values object if it comes as string from prop or attribute.
    let rawValues: any = null;
    if (this.values && typeof this.values === 'object' && !Array.isArray(this.values)) {
      rawValues = this.values;
    } else {
      const valuesRaw = typeof this.values === 'string' ? this.values : this.el.getAttribute('values');
      if (valuesRaw) {
        rawValues = this.parseJsonString(valuesRaw);
        if (!rawValues) {
          console.error('Failed to parse values:', valuesRaw);
        }
      }
    }

    if (rawValues) {
      this.values = rawValues;
    }

  }

  componentWillLoad() {
    this.parseValues();
    const hasSlides = this.slides && this.slides.length > 0;
    const hasValuesItems = this.values && this.values.items && this.values.items.length > 0;
    const hasItems = this.items && Array.isArray(this.items) && this.items.length > 0;

    if (!hasSlides && !hasValuesItems && !hasItems) {
      return;
    }
  }

  componentDidLoad() {
    // Re-parse in case values were set after componentWillLoad
    this.parseValues();
    const slides = this.getSlides();
    if (this.autoplay && slides.length > 1) {
      this.startAutoplay();
    }
  }

  private getSlides() {
    // Priority: values.items > items prop > slides
    if (this.values && this.values.items && this.values.items.length > 0) {
      return this.values.items.map(item => ({
        heading: item.question,
        text: item.raw_html ?? item.answer, // Use raw_html if available for better SSR performance
        mediaId: item.mediaId ? `screen${String(item.mediaId).padStart(2, '0')}` : undefined,
      }));
    }

    if (this.items && Array.isArray(this.items) && this.items.length > 0) {
      return this.items.map(item => ({
        heading: item.question,
        text: item.raw_html ?? item.answer, // Use raw_html if available for better SSR performance
        mediaId: item.mediaId ? `screen${String(item.mediaId).padStart(2, '0')}` : undefined,
      }));
    }

    return this.slides || [];
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  private startAutoplay() {
    if (!Build.isBrowser) return;
    this.stopAutoplay();
    this.autoplayTimer = window.setInterval(() => {
      this.nextSlide();
    }, this.autoplayInterval);
  }

  private stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = undefined;
    }
  }

  private goToSlide(index: number) {
    const slides = this.getSlides();
    if (this.isTransitioning || index === this.currentSlide) return;
    if (index < 0 || index >= slides.length) return;

    this.isTransitioning = true;
    this.currentSlide = index;

    // Wait for the next frame to ensure DOM is updated with transitioning class
    requestAnimationFrame(() => {
      // Then wait for transition duration before removing transitioning class
      // This ensures smooth fade from 0.7 to 1.0 opacity
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.isTransitioning = false;
        }, 300);
      });
    });

    if (this.autoplay) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  private nextSlide() {
    const slides = this.getSlides();
    const nextIndex = (this.currentSlide + 1) % slides.length;
    this.goToSlide(nextIndex);
  }

  private prevSlide() {
    const slides = this.getSlides();
    const prevIndex = (this.currentSlide - 1 + slides.length) % slides.length;
    this.goToSlide(prevIndex);
  }

  private handleTouchStart = (e: TouchEvent) => {
    this.touchStartX = e.touches[0].clientX;
    this.stopAutoplay();
  };

  private handleTouchMove = (e: TouchEvent) => {
    this.touchEndX = e.touches[0].clientX;
  };

  private handleTouchEnd = () => {
    if (!this.touchStartX || !this.touchEndX) return;

    const diff = this.touchStartX - this.touchEndX;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        // Swipe left - next slide
        this.nextSlide();
      } else {
        // Swipe right - previous slide
        this.prevSlide();
      }
    }

    this.touchStartX = 0;
    this.touchEndX = 0;

    if (this.autoplay) {
      this.startAutoplay();
    }
  };

  render() {
    const slides = this.getSlides();
    if (!slides || slides.length === 0) {
      return null;
    }

    const currentSlide = slides[this.currentSlide];
    const hasMultipleSlides = slides.length > 1;

    // Check if in How-To mode (either via values.items or items prop)
    const isHowToMode = !!((this.values && this.values.items) || (this.items && Array.isArray(this.items)));
    const containerClass = isHowToMode ? 'how-to-sign-slider slick-initialized' : COMPONENT_NAME;

    // Get title and subtitle from either values or direct props
    const displayTitle = this.values?.title || this.heading;
    const displaySubtitle = this.values?.subTitle || this.subtitle;

    return (
      <me-section yGap="2xl" yGapLg="5xl" class={containerClass} role="region" aria-label="Feature Carousel">
        <me-row yGap="4xl" yGapLg="6xl">
          <me-column span="12" class={`${COMPONENT_NAME}__intro`}>
            {isHowToMode ? (
              <div>
                {displayTitle && (
                  <me-heading variant="2" align="center" tag={this.headingTag}>
                    {displayTitle}
                  </me-heading>
                )}
                {displaySubtitle && (
                  <me-text variant="medium" align="center">
                    {displaySubtitle}
                  </me-text>
                )}
              </div>
            ) : (
              [<slot name="heading"></slot>, <slot name="description"></slot>]
            )}
          </me-column>
          <me-column span="12">
            <div
              class={`${COMPONENT_NAME}__carousel ${isHowToMode ? 'slick-list' : ''}`}
              onTouchStart={this.handleTouchStart}
              onTouchMove={this.handleTouchMove}
              onTouchEnd={this.handleTouchEnd}
              onMouseEnter={() => this.autoplay && this.stopAutoplay()}
              onMouseLeave={() => this.autoplay && this.startAutoplay()}
            >
              <div class={`${COMPONENT_NAME}__slide ${this.isTransitioning ? `${COMPONENT_NAME}__slide--transitioning` : ''} ${isHowToMode ? 'slick-slide slick-current' : ''}`}>
                <me-row align="center">
                  <me-column span="12" spanLg="6" class={`${COMPONENT_NAME}__media`}>
                    {isHowToMode && currentSlide.mediaId ? (
                      <sn-how-to-decoration mediaId={currentSlide.mediaId} />
                    ) : (
                      currentSlide.media && (
                        <me-picture aspectRatio="1/1" src={currentSlide.media} alt={currentSlide.heading || 'Feature image'} class={`${COMPONENT_NAME}__image`} />
                      )
                    )}
                  </me-column>
                  <me-column span="12" spanLg="6" class={`${COMPONENT_NAME}__content`}>
                    {currentSlide.heading && (
                      <me-heading variant="5" class={`${COMPONENT_NAME}__heading`} tag={this.itemHeadingTag}>
                        {currentSlide.heading}
                      </me-heading>
                    )}
                    {currentSlide.text && <div class={`${COMPONENT_NAME}__text`} innerHTML={currentSlide.text}></div>}
                  </me-column>
                </me-row>
              </div>

              {hasMultipleSlides && (
                <div class={`${COMPONENT_NAME}__navigation`}>
                  <div class={`${COMPONENT_NAME}__dots`} role="tablist" aria-label="Carousel navigation">
                    {slides.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        role="tab"
                        aria-selected={index === this.currentSlide ? 'true' : 'false'}
                        aria-label={`Go to slide ${index + 1}`}
                        class={`${COMPONENT_NAME}__dot ${index === this.currentSlide ? `${COMPONENT_NAME}__dot--active` : ''}`}
                        onClick={() => this.goToSlide(index)}
                      />
                    ))}
                  </div>
                  <div class={`${COMPONENT_NAME}__arrows`}>
                    <me-button
                      tag="button"
                      variant="ghost"
                      size="sm"
                      ariaLabel="Previous slide"
                      icon="arrows--chevron-left"
                      iconPosition="only"
                      onClick={() => this.prevSlide()}
                      class={`${COMPONENT_NAME}__arrow ${COMPONENT_NAME}__arrow--prev`}
                    />
                    <me-button
                      tag="button"
                      variant="ghost"
                      size="sm"
                      ariaLabel="Next slide"
                      icon="arrows--chevron-right"
                      iconPosition="only"
                      onClick={() => this.nextSlide()}
                      class={`${COMPONENT_NAME}__arrow ${COMPONENT_NAME}__arrow--next`}
                    />
                  </div>
                </div>
              )}
            </div>
          </me-column>
        </me-row>
      </me-section>
    );
  }
}
